import { useState, useRef, useCallback, useEffect } from "react";
import IconButton from "@/components/IconButton";
import Button from "@/components/Button";

interface CropModalProps {
  imageData: string;
  onCrop: (croppedImageData: string) => void;
  onCancel: () => void;
  hasTransparentBackground?: boolean;
}

interface CropArea {
  x: number;
  y: number;
  size: number;
}

const MIN_CROP_SIZE = 500;

export default function CropModal({ imageData, onCrop, onCancel, hasTransparentBackground = false }: CropModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);
  const [cropArea, setCropArea] = useState<CropArea>({ x: 0, y: 0, size: 0 });

  const [interaction, setInteraction] = useState<"none" | "drag" | "resize">("none");
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startCrop, setStartCrop] = useState<CropArea>({ x: 0, y: 0, size: 0 });
  const [resizeDirection, setResizeDirection] = useState<{ x: -1 | 1; y: -1 | 1 } | null>(null);

  useEffect(() => {
    if (!imageLoaded || !imageRef.current || !containerRef.current) return;

    const img = imageRef.current;
    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();

    const maxWidth = containerRect.width - 32;
    const maxHeight = containerRect.height - 32;

    const widthRatio = maxWidth / img.naturalWidth;
    const heightRatio = maxHeight / img.naturalHeight;
    const fitScale = Math.min(widthRatio, heightRatio, 1);

    const displayW = img.naturalWidth * fitScale;
    const displayH = img.naturalHeight * fitScale;

    setDisplaySize({ width: displayW, height: displayH });
    setScale(fitScale);

    const maxCropDisplay = Math.min(displayW, displayH) * 0.8;
    const minCropDisplay = MIN_CROP_SIZE * fitScale;
    const initialSize = Math.max(maxCropDisplay, minCropDisplay);

    setCropArea({
      x: (displayW - initialSize) / 2,
      y: (displayH - initialSize) / 2,
      size: initialSize
    });
  }, [imageLoaded]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const getRelativePosition = useCallback((clientX: number, clientY: number) => {
    if (!imageContainerRef.current) return { x: 0, y: 0 };
    const rect = imageContainerRef.current.getBoundingClientRect();
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }, []);

  const constrainCropArea = useCallback(
    (area: CropArea): CropArea => {
      const minSize = MIN_CROP_SIZE * scale;
      const maxSize = Math.min(displaySize.width, displaySize.height);

      let size = Math.max(minSize, Math.min(area.size, maxSize));
      let x = Math.max(0, Math.min(area.x, displaySize.width - size));
      let y = Math.max(0, Math.min(area.y, displaySize.height - size));

      return { x, y, size };
    },
    [displaySize, scale]
  );

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

    const pos = getRelativePosition(clientX, clientY);
    setStartPos(pos);
    setStartCrop({ ...cropArea });
    setInteraction("drag");
  };

  const handleResizeStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

    const pos = getRelativePosition(clientX, clientY);
    const centerX = cropArea.x + cropArea.size / 2;
    const centerY = cropArea.y + cropArea.size / 2;
    setResizeDirection({
      x: pos.x >= centerX ? 1 : -1,
      y: pos.y >= centerY ? 1 : -1
    });
    setStartPos(pos);
    setStartCrop({ ...cropArea });
    setInteraction("resize");
  };

  const handleMove = useCallback(
    (clientX: number, clientY: number) => {
      if (interaction === "none") return;

      const pos = getRelativePosition(clientX, clientY);
      const deltaX = pos.x - startPos.x;
      const deltaY = pos.y - startPos.y;

      if (interaction === "drag") {
        setCropArea(
          constrainCropArea({
            x: startCrop.x + deltaX,
            y: startCrop.y + deltaY,
            size: startCrop.size
          })
        );
      } else if (interaction === "resize") {
        if (!resizeDirection) return;
        const projectedX = deltaX * resizeDirection.x;
        const projectedY = deltaY * resizeDirection.y;
        const sizeDelta =
          projectedX >= 0 && projectedY >= 0 ? Math.max(projectedX, projectedY) : Math.min(projectedX, projectedY);
        const newSize = startCrop.size + sizeDelta;
        const newX = resizeDirection.x === -1 ? startCrop.x + (startCrop.size - newSize) : startCrop.x;
        const newY = resizeDirection.y === -1 ? startCrop.y + (startCrop.size - newSize) : startCrop.y;
        setCropArea(
          constrainCropArea({
            x: newX,
            y: newY,
            size: newSize
          })
        );
      }
    },
    [interaction, startPos, startCrop, resizeDirection, getRelativePosition, constrainCropArea]
  );

  const handleEnd = useCallback(() => {
    setInteraction("none");
    setResizeDirection(null);
  }, []);

  useEffect(() => {
    if (interaction === "none") return;

    const onMouseMove = (e: MouseEvent) => {
      handleMove(e.clientX, e.clientY);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const onEnd = () => {
      handleEnd();
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onEnd);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onEnd);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onEnd);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [interaction, handleMove, handleEnd]);

  const handleApplyCrop = () => {
    if (!imageRef.current) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const naturalX = cropArea.x / scale;
    const naturalY = cropArea.y / scale;
    const naturalSize = cropArea.size / scale;
    const outputSize = Math.max(naturalSize, MIN_CROP_SIZE);

    canvas.width = outputSize;
    canvas.height = outputSize;
    ctx.drawImage(imageRef.current, naturalX, naturalY, naturalSize, naturalSize, 0, 0, outputSize, outputSize);

    const format = hasTransparentBackground ? "image/png" : "image/jpeg";
    onCrop(canvas.toDataURL(format, hasTransparentBackground ? undefined : 0.92));
  };

  const actualCropSize = Math.round(cropArea.size / scale);

  return (
    <div
      className="fixed inset-0 bg-gradient-to-b from-black/95 via-black/90 to-black/95 z-[60] flex flex-col"
      data-testid="crop-modal"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 p-4 md:p-6 border-b border-white/10 backdrop-blur">
        <div className="flex items-center gap-2">
          <IconButton name="close" onClick={onCancel} title="Cancel" iconClassName="text-white" />
          <span className="text-label-medium text-white/70 hidden sm:inline">Cancel</span>
        </div>
        <div className="text-center">
          <h2 className="text-title-medium text-white tracking-wide">Crop Photo</h2>
          <p className="text-body-small text-gray-400">
            {actualCropSize} x {actualCropSize} px
            {actualCropSize < MIN_CROP_SIZE && <span className="text-yellow-400 ml-2">(min: {MIN_CROP_SIZE}px)</span>}
          </p>
        </div>
        <Button variant="filled" onClick={handleApplyCrop}>
          Apply
        </Button>
      </div>

      {/* Crop area */}
      <div ref={containerRef} className="flex-1 flex items-center justify-center p-4 md:p-8 overflow-hidden">
        <div className="relative rounded-2xl ring-1 ring-white/10 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)]">
          <div
            ref={imageContainerRef}
            className={`relative rounded-2xl overflow-hidden ${hasTransparentBackground ? "bg-white" : "bg-black/20"}`}
            style={{ width: displaySize.width, height: displaySize.height }}
          >
            {/* Image */}
            <img
              ref={imageRef}
              src={imageData}
              alt="Crop preview"
              onLoad={handleImageLoad}
              className="absolute inset-0 select-none"
              style={{ width: displaySize.width, height: displaySize.height }}
              draggable={false}
            />

            {/* Dark overlay outside crop area */}
            {imageLoaded && displaySize.width > 0 && (
              <>
                {/* Top */}
                <div
                  className="absolute bg-black/60 left-0 right-0 top-0 pointer-events-none"
                  style={{ height: Math.max(0, cropArea.y) }}
                />
                {/* Bottom */}
                <div
                  className="absolute bg-black/60 left-0 right-0 bottom-0 pointer-events-none"
                  style={{ height: Math.max(0, displaySize.height - cropArea.y - cropArea.size) }}
                />
                {/* Left */}
                <div
                  className="absolute bg-black/60 left-0 pointer-events-none"
                  style={{
                    top: cropArea.y,
                    width: Math.max(0, cropArea.x),
                    height: cropArea.size
                  }}
                />
                {/* Right */}
                <div
                  className="absolute bg-black/60 right-0 pointer-events-none"
                  style={{
                    top: cropArea.y,
                    width: Math.max(0, displaySize.width - cropArea.x - cropArea.size),
                    height: cropArea.size
                  }}
                />

                {/* Crop selection box */}
                <div
                  className="absolute cursor-move select-none"
                  style={{
                    left: cropArea.x,
                    top: cropArea.y,
                    width: cropArea.size,
                    height: cropArea.size,
                    touchAction: "none"
                  }}
                  onMouseDown={handleDragStart}
                  onTouchStart={handleDragStart}
                >
                  {/* Grid lines - rule of thirds */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/50" />
                    <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/50" />
                    <div className="absolute top-1/3 left-0 right-0 h-px bg-white/50" />
                    <div className="absolute top-2/3 left-0 right-0 h-px bg-white/50" />
                  </div>

                  {/* Corner brackets - Top Left */}
                  <div
                    className="absolute -top-1 -left-1 w-10 h-10 cursor-nwse-resize"
                    onMouseDown={handleResizeStart}
                    onTouchStart={handleResizeStart}
                    style={{ touchAction: "none" }}
                  >
                    <div className="absolute top-0 left-0 w-7 h-[3px] bg-white rounded-full" />
                    <div className="absolute top-0 left-0 w-[3px] h-7 bg-white rounded-full" />
                  </div>

                  {/* Corner brackets - Top Right */}
                  <div
                    className="absolute -top-1 -right-1 w-10 h-10 cursor-nesw-resize"
                    onMouseDown={handleResizeStart}
                    onTouchStart={handleResizeStart}
                    style={{ touchAction: "none" }}
                  >
                    <div className="absolute top-0 right-0 w-7 h-[3px] bg-white rounded-full" />
                    <div className="absolute top-0 right-0 w-[3px] h-7 bg-white rounded-full" />
                  </div>

                  {/* Corner brackets - Bottom Left */}
                  <div
                    className="absolute -bottom-1 -left-1 w-10 h-10 cursor-nesw-resize"
                    onMouseDown={handleResizeStart}
                    onTouchStart={handleResizeStart}
                    style={{ touchAction: "none" }}
                  >
                    <div className="absolute bottom-0 left-0 w-7 h-[3px] bg-white rounded-full" />
                    <div className="absolute bottom-0 left-0 w-[3px] h-7 bg-white rounded-full" />
                  </div>

                  {/* Corner brackets - Bottom Right */}
                  <div
                    className="absolute -bottom-1 -right-1 w-10 h-10 cursor-nwse-resize"
                    onMouseDown={handleResizeStart}
                    onTouchStart={handleResizeStart}
                    style={{ touchAction: "none" }}
                  >
                    <div className="absolute bottom-0 right-0 w-7 h-[3px] bg-white rounded-full" />
                    <div className="absolute bottom-0 right-0 w-[3px] h-7 bg-white rounded-full" />
                  </div>

                  {/* Side handles - Left */}
                  <div
                    className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-12 cursor-ew-resize flex items-center justify-center"
                    onMouseDown={handleResizeStart}
                    onTouchStart={handleResizeStart}
                    style={{ touchAction: "none" }}
                  >
                    <div className="w-[3px] h-8 bg-white rounded-full" />
                  </div>

                  {/* Side handles - Right */}
                  <div
                    className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 w-6 h-12 cursor-ew-resize flex items-center justify-center"
                    onMouseDown={handleResizeStart}
                    onTouchStart={handleResizeStart}
                    style={{ touchAction: "none" }}
                  >
                    <div className="w-[3px] h-8 bg-white rounded-full" />
                  </div>

                  {/* Side handles - Top */}
                  <div
                    className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-6 cursor-ns-resize flex items-center justify-center"
                    onMouseDown={handleResizeStart}
                    onTouchStart={handleResizeStart}
                    style={{ touchAction: "none" }}
                  >
                    <div className="w-8 h-[3px] bg-white rounded-full" />
                  </div>

                  {/* Side handles - Bottom */}
                  <div
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-12 h-6 cursor-ns-resize flex items-center justify-center"
                    onMouseDown={handleResizeStart}
                    onTouchStart={handleResizeStart}
                    style={{ touchAction: "none" }}
                  >
                    <div className="w-8 h-[3px] bg-white rounded-full" />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer hint */}
      <div className="p-4 text-center">
        <p className="text-body-small text-gray-400">
          Drag to move, drag corners to resize. Minimum size: {MIN_CROP_SIZE}x{MIN_CROP_SIZE}px
        </p>
      </div>
    </div>
  );
}
